import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { UserType } from '../graphql/generated/graphql';

export enum GroupPolicyAction {
  RequestGroup,
  DeleteGroup,
  EditMembership,
  JoinGroup,
  RequestMembership,
  UpdateGroup,
  LeaveGroup,
}

const GROUP_POLICY_CHECK_USER_FRAGMENT = gql(`
  fragment groupPolicyCheck_user on User {
    id
    type
  }
`);

type UserFragment = DocumentType<typeof GROUP_POLICY_CHECK_USER_FRAGMENT>;

const GROUP_POLICY_CHECK_GROUP_FRAGMENT = gql(`
  fragment groupPolicyCheck_group on Group {
    id
    owners {
      id
    }
    members {
      id
    }
    requestors {
      id
    }
  }
`);

type GroupFragment = DocumentType<typeof GROUP_POLICY_CHECK_GROUP_FRAGMENT>;

const findUserId = <T extends { id: string }, U extends { id: string }>(
  arr: T[],
  user: U,
) => !!(user?.id && arr?.find((u) => u?.id === user?.id));

const isOwner = (group: GroupFragment, user: UserFragment) =>
  findUserId(group?.owners, user);

const isMember = (group: GroupFragment, user: UserFragment) =>
  findUserId(group?.members, user);

const isRequestor = (group: GroupFragment, user: UserFragment) =>
  findUserId(group?.requestors, user);

export const isUserInGroup = (group: GroupFragment, user: UserFragment) =>
  isOwner(group, user) || isMember(group, user);

function groupPolicyCheck(
  action: GroupPolicyAction,
  user: UserFragment,
  group?: GroupFragment | null,
) {
  if (action === GroupPolicyAction.RequestGroup) {
    return user.type !== UserType.Guest;
  }

  if (!group) {
    return false;
  }

  switch (action) {
    case GroupPolicyAction.JoinGroup:
      return (
        !(isOwner(group, user) || isMember(group, user)) &&
        user.type === UserType.Admin
      );
    case GroupPolicyAction.EditMembership:
    case GroupPolicyAction.UpdateGroup:
    case GroupPolicyAction.DeleteGroup:
      return isOwner(group, user);
    case GroupPolicyAction.LeaveGroup:
      return isOwner(group, user) || isMember(group, user);
    case GroupPolicyAction.RequestMembership:
      return (
        user.type !== UserType.Guest &&
        !isUserInGroup(group, user) &&
        !isRequestor(group, user)
      );
    default:
      return false;
  }
}

export default groupPolicyCheck;
